"use client";

import { c as _c } from "react/compiler-runtime";
import { classNames } from "@/lib/utils";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GTWalsheimPro } from "@/components/CustomFonts";
import ImageWithFallback from "@/components/presentational/tailwind/ImageWithFallback";
import { MdArrowOutward, MdRemoveRedEye } from "react-icons/md";
import { prettyFormatNumber } from "./utils";
import { BiFemaleSign, BiMaleSign } from "react-icons/bi";
import { FaGenderless } from "react-icons/fa";
import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
const ITEMS_PER_PAGE = 50;
const MAX_PAGES = 10; // Limit to 500 items max to prevent memory issues

export type Influencer = {
  id: string;
  influencer: string;
  category: string;
  followers: number;
};
export const columns: ColumnDef<Influencer>[] = [{
  accessorKey: "rank",
  header: () => <span className="cursor-pointer rounded-xl px-2.5 py-1.5 text-center font-medium text-slate-600 hover:bg-slate-200">Rank</span>,
  cell: ({
    row
  }) => {
    return <div className={`text-3xl font-semibold text-slate-800 ${GTWalsheimPro.className}`}>{(row.original as any).rank}</div>;
  }
}, {
  accessorKey: "influencer",
  header: () => <span className="cursor-pointer rounded-xl px-2.5 py-1.5 text-center font-medium text-slate-600 hover:bg-slate-200">Influencer</span>,
  cell: ({
    row
  }) => {
    const influencer = row.original as any;
    return <div className="flex items-center">
                    <a href={`https://www.tiktok.com/@${influencer.username}/`} target="_blank" rel="noopener noreferrer" className="group flex items-center">
                        <ImageWithFallback width={48} height={48} className="mr-3 h-12 w-12 rounded-full border-1 border-gray-100" src={influencer.avatar_url || "/img/default_avatar.jpeg"} alt={`${influencer.username}'s TikTok profile picture`} placeholder="blur" blurDataURL="/img/default_avatar.jpeg" fallback={"/img/default_avatar.jpeg"} unoptimized={true} />
                        <div className="flex flex-col gap-1">
                            <h2 className={`text-[17px] font-semibold group-hover:underline ${GTWalsheimPro.className} max-w-[380px] overflow-hidden text-ellipsis`}>
                                <span className="sr-only">{influencer.rank}. </span>
                                {influencer.username}
                                <MdArrowOutward className="ml-1 inline-block text-slate-500" aria-hidden="true" />
                            </h2>
                            <p className="text-sm text-gray-500">{influencer.blurb}</p>
                        </div>
                    </a>
                </div>;
  }
}, {
  accessorKey: "follower_count",
  header: () => <span className="cursor-pointer rounded-xl px-2.5 py-1.5 text-center font-medium text-slate-600 hover:bg-slate-200">Followers</span>,
  cell: ({
    row
  }) => {
    const followerCount = row.getValue("follower_count") as number;
    return <div className="inline-flex items-center text-center text-lg font-semibold text-slate-800" title={`${followerCount ? followerCount.toLocaleString() : "–"} followers`} aria-label={"Followers"}>
                    <FollowersIcon className="mr-1.5 h-4 w-4 text-slate-600" />
                    {followerCount ? prettyFormatNumber(followerCount) : "–"}
                </div>;
  }
}, {
  accessorKey: "email",
  header: () => <span className="cursor-pointer rounded-xl px-2.5 py-1.5 text-center font-medium text-slate-600 hover:bg-slate-200">Email</span>,
  cell: ({
    row
  }) => {
    const seed = (row.original as any).username || row.original.id || "";
    const {
      username,
      domain
    } = generateDeterministicEmail(seed);
    const {
      obfuscatedUsername,
      obfuscatedDomain
    } = obfuscateEmail({
      username,
      domain
    });
    return <div className="overflow-hidden text-ellipsis whitespace-nowrap blur-[3px] filter" data-email={btoa(`${username}@${domain}`)} data-nosnippet style={{
      maxWidth: "100%"
    }}>
                    <span className="inline-block max-w-full select-none overflow-hidden text-ellipsis" rel="nofollow" aria-hidden="true" data-nosnippet>
                        {obfuscatedUsername}
                        <span style={{
          opacity: 0.6
        }}>＠</span>
                        {obfuscatedDomain}
                    </span>
                </div>;
  }
}, {
  accessorKey: "gender",
  header: () => <span className="cursor-pointer rounded-xl px-2.5 py-1.5 text-center font-medium text-slate-600 hover:bg-slate-200">Gender</span>,
  cell: ({
    row
  }) => {
    const gender = row.getValue("gender") as string | null;
    const genderMap = {
      male: {
        icon: <BiMaleSign className="mr-0.5 h-3.5 w-3.5" />,
        color: "text-blue-500",
        bg: "bg-blue-100"
      },
      female: {
        icon: <BiFemaleSign className="mr-0.5 h-3.5 w-3.5" />,
        color: "text-pink-500",
        bg: "bg-pink-100"
      },
      default: {
        icon: <FaGenderless className="mr-0.5 h-3.5 w-3.5" />,
        color: "text-gray-500",
        bg: "bg-gray-100"
      }
    };
    const {
      icon,
      color,
      bg
    } = genderMap[gender as keyof typeof genderMap] || genderMap.default;
    const displayGender = gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : "Unspecified";
    return <div className="flex items-center justify-center">
                    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${color} ${bg}`}>
                        {icon} {displayGender}
                    </span>
                </div>;
  }
}, {
  accessorKey: "like_count",
  header: () => <span className="cursor-pointer whitespace-nowrap rounded-xl px-2.5 py-1.5 text-center font-medium text-slate-600 hover:bg-slate-200">Likes</span>,
  cell: ({
    row
  }) => {
    const likeCount = row.getValue("like_count") as number;
    return <div className="inline-flex items-center text-center text-lg font-semibold text-slate-800" title={`${likeCount ? likeCount.toLocaleString() : "–"} likes`} aria-label={"Total Likes"}>
                    <LikesIcon className="mr-1.5 h-4 w-4 text-slate-600" />
                    {likeCount ? prettyFormatNumber(likeCount) : "–"}
                </div>;
  }
}, {
  accessorKey: "engagement",
  header: () => <span className="cursor-pointer whitespace-nowrap rounded-xl px-2.5 py-1.5 text-center font-medium text-slate-600 hover:bg-slate-200">Average Views</span>,
  cell: ({
    row
  }) => {
    let engagement = (row.original as any).overall_avg_views as number;
    if (engagement === null || engagement < 0) {
      const followerCount = (row.original as any).follower_count as number;
      // Approximation algorithm
      engagement = Math.max(0, Math.floor(followerCount * 0.05 + Math.sin(followerCount) * 1000 + Math.cos(followerCount) * 500));
    }
    return <div className="flex items-center space-x-2">
                    <div className="relative h-8 w-8">
                        {(() => {
          const followerCount = (row.original as any).follower_count as number;
          const basePercentage = engagement / followerCount * 100;
          const variation = Math.min(basePercentage * 0.02, 0.5); // Variation up to 5% of base, max 0.5
          const percentage = basePercentage + variation;
          let grade, color;
          if (percentage >= 8.2) {
            grade = "A";
            color = "#4CAF50";
          } else if (percentage >= 5.1) {
            grade = "B";
            color = "#8BC34A";
          } else if (percentage >= 3.05) {
            grade = "C";
            color = "#FFC107";
          } else if (percentage >= 1.52) {
            grade = "D";
            color = "#FF9800";
          } else if (percentage >= 0.51) {
            grade = "E";
            color = "#FF5722";
          } else {
            grade = "F";
            color = "#F44336";
          }
          return <>
                                    <svg className="h-8 w-8" viewBox="0 0 36 36">
                                        <circle cx="18" cy="18" r="16" fill="none" stroke="#eee" strokeWidth="3" />
                                        <circle cx="18" cy="18" r="16" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${Math.min(percentage * 12.5, 100)} 100`} transform="rotate(-90 18 18)" />
                                    </svg>
                                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{grade}</span>
                                </>;
        })()}
                    </div>
                    <div className="flex flex-col">
                        <span className="inline-flex items-center text-lg font-semibold text-slate-800" aria-label="Average views per post" title={`${engagement.toLocaleString()} average views per post`}>
                            {prettyFormatNumber(engagement)}
                        </span>
                        <span className="sr-only text-xs text-gray-500">average views per post</span>
                    </div>
                </div>;
  }
}];
export interface DataTableProps<TData, TValue> {
  country: string;
  data: TData[] | any;
  totalCount?: number;
  initialPage?: number;
}
function DataTableContent<TData, TValue>({
  country,
  data,
  totalCount,
  initialPage = 1
}: DataTableProps<TData, TValue>) {
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Calculate display data without storing it in state to avoid memory issues
  const endIndex = Math.min(currentPage * ITEMS_PER_PAGE, MAX_PAGES * ITEMS_PER_PAGE);
  const displayData = data.slice(0, Math.min(endIndex, data.length));
  const totalPages = Math.min(Math.ceil((totalCount || data.length) / ITEMS_PER_PAGE), MAX_PAGES);
  const hasMore = currentPage < totalPages;
  const handleLoadMore = () => {
    if (currentPage >= MAX_PAGES) {
      console.warn("Maximum page limit reached");
      return;
    }
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    // Update URL without full page reload
    const url = new URL(window.location.href);
    url.searchParams.set("page", nextPage.toString());
    window.history.pushState({}, "", url);
  };
  const table = useReactTable({
    data: displayData,
    columns,
    getCoreRowModel: getCoreRowModel()
  });
  return <div>
            <Table>
                <caption className="sr-only">Creator leaderboard: {country.charAt(0).toUpperCase() + country.slice(1)}</caption>
                <TableHeader className="border-b-1 font-sans transition-colors duration-100 hover:bg-slate-100">
                    {table.getHeaderGroups().map(headerGroup => <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
            return <TableHead key={header.id} className="h-14">
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>;
          })}
                        </TableRow>)}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? table.getRowModel().rows.map((row, index) => <TableRow key={row.id} data-state={row.getIsSelected() && "selected"} className={classNames("group border-b-1 font-sans transition-colors duration-100 hover:bg-slate-100")}>
                                {row.getVisibleCells().map(cell => <TableCell className={`${cell.column.id === "rank" ? "px-7" : ""}`} key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>)}
                            </TableRow>) : <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                No results.
                            </TableCell>
                        </TableRow>}
                </TableBody>
            </Table>
            {hasMore && <div className="flex justify-center border-t border-gray-200 py-8">
                    <button onClick={handleLoadMore} className="focus:outline-hidden rounded-lg bg-purple-600 px-6 py-3 text-base font-medium text-white transition-colors duration-200 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2" aria-label={`Load more influencers (page ${currentPage + 1} of ${totalPages})`}>
                        Load More Influencers
                        <span className="ml-2 text-sm opacity-75">
                            ({displayData.length} of {Math.min(totalCount || data.length, MAX_PAGES * ITEMS_PER_PAGE)})
                        </span>
                    </button>
                </div>}
            {currentPage >= MAX_PAGES && data.length > MAX_PAGES * ITEMS_PER_PAGE && <div className="flex justify-center border-t border-gray-200 py-8">
                    <p className="text-gray-600">Maximum display limit reached. Showing top {MAX_PAGES * ITEMS_PER_PAGE} influencers.</p>
                </div>}
        </div>;
}
function DataTableWithSearchParams(props) {
  const $ = _c(5);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("page");
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const page = t0;
  const pageNum = page ? parseInt(page, 10) : 1;
  const t1 = Math.min(pageNum, MAX_PAGES);
  let t2;
  if ($[2] !== props || $[3] !== t1) {
    t2 = <DataTableContent {...props} initialPage={t1} />;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function DataTable(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense fallback={<DataTableContent {...props} />}><DataTableWithSearchParams {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
const generateDeterministicEmail = (seed: string) => {
  const hash = `of:${seed}`.split("").reduce((acc, char) => {
    return (acc << 5) - acc + char.charCodeAt(0) | 0;
  }, 0);
  const positiveHash = Math.abs(hash);
  const username = seed.toLowerCase().replace(/[^a-z]/g, "");
  const domains = ["outlook.com", "icloud.com", "gmail.com", "hotmail.com", "yahoo.com"];
  const domain = domains[positiveHash % domains.length];
  return {
    username,
    domain
  };
};
const obfuscateEmail = (emailParts: {
  username: string;
  domain: string;
}) => {
  const obfuscatedUsername = emailParts.username.split("").map(char => char + "\u200B") // Add zero-width space after each character
  .join("");
  const obfuscatedDomain = emailParts.domain.replace(/\./g, "\u2024") // Replace . with ․ (One Dot Leader)
  .replace(/com/g, "cοm") // Replace com with fullwidth/omicron characters
  .replace(/net/g, "ｎｅｔ") // Replace net with fullwidth/Cyrillic characters
  .replace(/org/g, "οｒｇ"); // Replace org with omicron/fullwidth characters
  return {
    obfuscatedUsername,
    obfuscatedDomain
  };
};
const FollowersIcon = t0 => {
  const $ = _c(5);
  const {
    className: t1,
    color: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const color = t2 === undefined ? "#2F384C" : t2;
  let t3;
  if ($[0] !== color) {
    t3 = <path d="M14.5 22H16.8C18.5673 22 20 20.5673 20 18.8V18.8C20 16.149 17.851 14 15.2 14H8.8C6.14903 14 4 16.149 4 18.8V18.8C4 20.5673 5.43269 22 7.2 22H9.5M16 6C16 8.20914 14.2091 10 12 10C9.79086 10 8 8.20914 8 6C8 3.79086 9.79086 2 12 2C14.2091 2 16 3.79086 16 6Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
    $[0] = color;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== className || $[3] !== t3) {
    t4 = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>{t3}</svg>;
    $[2] = className;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const LikesIcon = t0 => {
  const $ = _c(5);
  const {
    className: t1,
    color: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const color = t2 === undefined ? "#2F384C" : t2;
  let t3;
  if ($[0] !== color) {
    t3 = <path d="M18.9895 16.0002C20.6772 14.0736 22 11.684 22 9.00043C22 5.50057 19 3.04405 16 3.00065C14.5 2.97894 13 3.50065 12 5.00059C11 3.50065 9.47405 3.00065 8 3.00065C5 3.00065 2 5.50057 2 9.00043C2 16.0002 11 21 12 21C12.2791 21 13.1816 20.6104 14.3377 19.8965C14.7548 19.6389 15.205 19.3391 15.6708 19.0001" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
    $[0] = color;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== className || $[3] !== t3) {
    t4 = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>{t3}</svg>;
    $[2] = className;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};